import {
  computed,
  ComputedRef,
  defineAsyncComponent,
  inject,
  InjectionKey,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  Ref,
  unref,
  watchEffect,
} from 'vue';
import { useModal } from 'vue-final-modal';

import { defineAppPlugin } from '@/main';
import isFunction from '@/utils/isFunction';
import isNullOrUndefined from '@/utils/isNullOrUndefined';
import wrapArray from '@/utils/wrapArray';

const LazyAppSpinner = defineAsyncComponent(
  () => import('@/components/molecules/AppSpinner.vue')
);

const INJECTION_KEY: InjectionKey<{
  collection: Ref<Set<ComputedRef<boolean>>>;
}> = Symbol();

export default defineAppPlugin(context => {
  const collection = ref(new Set<ComputedRef<boolean>>());

  const modal = useModal({
    attrs: {
      class: 'fixed inset-0 z-50 flex items-center justify-center',
      clickToClose: false,
      escToClose: false,
    },
    slots: {
      default: LazyAppSpinner,
    },
  });

  watchEffect(() => {
    const values = [...collection.value].map(item => item.value);
    values.some(Boolean) ? modal.open() : modal.close();
  });

  context.app.provide(INJECTION_KEY, {
    collection,
  });
});

/**
 *
 * @param value 觀察的狀態列表
 */
export function useSpinner(
  value?:
    | MaybeArray<Ref<boolean> | (() => boolean)>
    | (() => MaybeArray<boolean>)
) {
  const context = inject(INJECTION_KEY);

  const state = computed(() => {
    const values = !isNullOrUndefined(value)
      ? isFunction(value)
        ? wrapArray(value())
        : wrapArray(value).map(v => (isFunction(v) ? v() : unref(v)))
      : [false];

    return values.some(i => i);
  });

  onMounted(() => {
    context?.collection.value.add(state);
  });

  onBeforeUnmount(async () => {
    await nextTick();
    context?.collection.value.delete(state);
  });

  return state;
}
